// pages/Personal Attendance/components/DirectReportPicker.js
//
// Go-live parity (2026-09-24), ATT#6: legacy frmFtoRequest let a supervisor
// file an FTO / COA for one of their active subordinates (frmFtoRequest.vb
// 1273-1306, 1696-1800) — how hourly staff without a login got time off.
// Shown on the NEW FTO / NEW COA forms only. The server re-checks the scope
// on submit and records the supervisor stage as approved when the filer is
// the employee's direct supervisor.
//
// Owner 2026-10-14: "previously a manager could put a COA for their people".
// The picker was a small dropdown that only appeared for a supervisor with
// direct reports, so HR / IT Admin never saw it. It is now a clear
// "Request for" block, like the legacy Employee ID + Lookup: Myself, or an
// employee found by name or number. GET /api/fto/direct-reports returns the
// people the caller may file for (direct reports, or everyone for a
// "see all" caller).

import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Label } from "reactstrap";
import { getMyDirectReports } from "../ftoService";

const MAX_SHOWN = 12;

const DirectReportPicker = ({ enabled = true, selfEmpId, value, onChange }) => {
    const [reports, setReports] = useState([]);
    const [forOther, setForOther] = useState(false);
    const [query, setQuery] = useState("");

    useEffect(() => {
        if (!enabled) { setReports([]); return undefined; }
        let cancelled = false;
        getMyDirectReports()
            .then(({ data }) => { if (!cancelled) setReports(Array.isArray(data) ? data : []); })
            .catch(() => { if (!cancelled) setReports([]); });
        return () => { cancelled = true; };
    }, [enabled]);

    const idOf = (r) => String(r?.empId ?? r?.EmpId ?? "");
    const nameOf = (r) => r?.name ?? r?.Name ?? "";

    const self = String(selfEmpId || "");
    const current = String(value || self);
    const selected = current && current !== self ? reports.find((r) => idOf(r) === current) : null;

    // the form may already hold another employee (for example after a page refresh)
    useEffect(() => {
        if (current && current !== self) setForOther(true);
    }, [current, self]);

    const matches = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return reports.slice(0, MAX_SHOWN);
        return reports
            .filter((r) => idOf(r).toLowerCase().includes(q) || nameOf(r).toLowerCase().includes(q))
            .slice(0, MAX_SHOWN);
    }, [reports, query]);

    if (!enabled || reports.length === 0) return null;

    const chooseSelf = () => {
        setForOther(false);
        setQuery("");
        onChange(self, undefined);
    };
    const chooseOther = () => setForOther(true);
    const pick = (r) => {
        setQuery("");
        onChange(idOf(r), nameOf(r));
    };

    return (
        <div className="mb-3 rounded p-2" style={{ background: "var(--ssw-tint-blue, #eef2ff)" }}>
            <Label className="mb-1" style={{ fontSize: 12 }}>Request for</Label>
            <div className="d-flex align-items-center gap-3 mb-2">
                <div className="form-check mb-0">
                    <Input type="radio" id="drp-self" name="drp-for" checked={!forOther} onChange={chooseSelf} />
                    <Label htmlFor="drp-self" check className="ms-1" style={{ fontSize: 13 }}>Myself ({self})</Label>
                </div>
                <div className="form-check mb-0">
                    <Input type="radio" id="drp-other" name="drp-for" checked={forOther} onChange={chooseOther} />
                    <Label htmlFor="drp-other" check className="ms-1" style={{ fontSize: 13 }}>An employee</Label>
                </div>
            </div>

            {forOther && (
                <div>
                    {selected ? (
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <span className="badge bg-primary" style={{ fontSize: 12 }}>
                                {nameOf(selected) || idOf(selected)} ({idOf(selected)})
                            </span>
                            <Button color="link" size="sm" className="p-0" style={{ fontSize: 12 }}
                                onClick={() => onChange(self, undefined)}>
                                change
                            </Button>
                        </div>
                    ) : (
                        <>
                            <Input
                                bsSize="sm" style={{ maxWidth: 340 }}
                                placeholder="Type a name or employee ID"
                                value={query}
                                autoFocus
                                onChange={(e) => setQuery(e.target.value)}
                            />
                            <div className="mt-1 d-flex flex-wrap gap-1">
                                {matches.map((r) => (
                                    <Button key={idOf(r)} color="light" size="sm" className="border"
                                        style={{ fontSize: 12 }} onClick={() => pick(r)}>
                                        {nameOf(r) || idOf(r)} <span className="text-muted">({idOf(r)})</span>
                                    </Button>
                                ))}
                                {matches.length === 0 && (
                                    <span className="text-muted" style={{ fontSize: 12 }}>No employee matches that.</span>
                                )}
                                {matches.length === MAX_SHOWN && reports.length > MAX_SHOWN && (
                                    <span className="text-muted align-self-center" style={{ fontSize: 11 }}>
                                        Keep typing to narrow the list.
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

            <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                Filing for one of your direct reports records your supervisor approval; the request then goes to HR.
            </div>
        </div>
    );
};

export default DirectReportPicker;
